import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import Modal from "../../components/Modal";
import Menu, { type MenuItem } from "../../components/Menu";
import api, { sessionService } from "../../utils/api";
import Pagination from "../../components/Pagination";
import { usePagination } from "../../hooks/usePagination";
import { PaginationParams, PageResponse } from "../../types";
import "./Sessions.css";
import { formatCurrencyAmount, getCurrencyInfo } from "../../utils/currency";

interface ParkingSession {
  sessionId: string;
  plateNumber: string;
  lotId: string;
  entryTime: string;
  exitTime: string | null;
  departed: boolean;
  forceEnd: boolean;
  totalAmount: number | null;
  currency?: string;
  ownerName?: string;
  entryAlprId?: string;
  exitAlprId?: string;
  advancedPricingEnabled?: boolean;
  pricingId?: string;
  vehicleRegistered?: boolean;
  sessionType?: string;
  userType?: string;
  vehicleType?: string;
  dwellTime?: number;
  dwellPeriodTimeScale?: string;
  paymentStatus?: string;
}

type SortDirection = "ascending" | "descending";
type SortKey =
  | "plateNumber"
  | "entryTime"
  | "status"
  | "duration"
  | "totalAmount"
  | "paymentStatus";

interface SortConfig {
  key: SortKey;
  direction: SortDirection;
}

const TABLE_PAGE_SIZE = 15;

const PAYMENT_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "NOT_REQUIRED", label: "No Charge" },
  { value: "PENDING", label: "Pending" },
  { value: "PAID_SINGLE", label: "Paid" },
  { value: "UNPAID", label: "Unpaid" },
  { value: "DISPUTED", label: "Disputed" },
  { value: "REFUNDED", label: "Refunded" },
];

const getPaymentStatusDisplayText = (paymentStatus?: string): string => {
  if (!paymentStatus) return "Unknown";
  const statusMap: Record<string, string> = {
    NOT_REQUIRED: "No Charge",
    PENDING: "Pending",
    INPROGRESS: "Processing",
    SESSION_STARTED: "Active Session",
    UNPAID: "Unpaid",
    PAID_SINGLE: "Paid",
    PAID_WALLET: "Paid",
    PAID_AUTOFILL: "Paid",
    FAILED: "Failed",
    EXPIRED: "Expired",
    CANCELLED: "Cancelled",
    REFUNDED: "Refunded",
    PARTIALLY_REFUNDED: "Refunded*",
    DISPUTED: "Disputed",
    CHARGEBACK: "Chargeback",
    REQUIRES_MANUAL_ACTION: "Please Fix",
    AUTO_REFUND_INITIATED: "Refunding",
  };
  return statusMap[paymentStatus] || paymentStatus;
};

const getPaymentStatusColor = (paymentStatus?: string): string => {
  if (!paymentStatus) return "gray";
  if (["NOT_REQUIRED", "PAID_SINGLE", "PAID_WALLET", "PAID_AUTOFILL"].includes(paymentStatus)) {
    return "green";
  }
  if (["PENDING", "INPROGRESS", "SESSION_STARTED"].includes(paymentStatus)) {
    return "yellow";
  }
  if (
    [
      "UNPAID",
      "FAILED",
      "EXPIRED",
      "CANCELLED",
      "REFUNDED",
      "PARTIALLY_REFUNDED",
      "DISPUTED",
      "CHARGEBACK",
      "REQUIRES_MANUAL_ACTION",
      "AUTO_REFUND_INITIATED",
    ].includes(paymentStatus)
  ) {
    return "red";
  }
  return "gray";
};

const formatSessionAmount = (amount: number | null, currency?: string): string => {
  if (amount === null || amount === undefined) {
    return "TBD";
  }
  const currencyInfo = getCurrencyInfo(currency);
  const formatted = formatCurrencyAmount(amount, currency) || amount.toFixed(2);

  if (currencyInfo.code === "MIXED") {
    return `${formatted} Mixed`;
  }

  // If we have a symbol, the formatted string already contains it; avoid repeating the code.
  if (currencyInfo.symbol) {
    return formatted;
  }

  // Fallback for unknown currencies where we don't have a symbol.
  return currencyInfo.code ? `${formatted} ${currencyInfo.code}` : formatted;
};

const formatDisplayLotId = (id: string) => {
  if (!id || id.length < 8) return id;
  const lastEight = id.slice(-8);
  return `${lastEight.slice(0, 4)}-${lastEight.slice(4)}`;
};

const Sessions: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "entryTime",
    direction: "descending",
  });
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<ParkingSession | null>(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFilename, setDownloadFilename] = useState("");
  const [lotDisplayId, setLotDisplayId] = useState("");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [isForceEnding, setIsForceEnding] = useState(false);
  const [isUpdatingPaymentStatus, setIsUpdatingPaymentStatus] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const copyToClipboard = useCallback((value?: string) => {
    if (!value) return;
    const fallbackCopy = () => {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch (error) {
        console.error("Failed to copy text", error);
      }
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  }, []);
  const handleCopy = useCallback(
    (value?: string, key?: string) => {
      if (!value || !key) return;
      copyToClipboard(value);
      setCopiedKey(key);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => setCopiedKey(null), 500);
    },
    [copyToClipboard]
  );

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const updateIsMobile = () => {
      if (typeof window === "undefined") {
        setIsMobile(false);
        return;
      }
      setIsMobile(window.innerWidth <= 768);
    };

    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  const getDefaultFilename = useCallback(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const formattedLot = formatDisplayLotId(lotDisplayId);
    return `${formattedLot || lotId || "LOT"}_SessionLog_${yyyy}-${mm}-${dd}.csv`;
  }, [lotDisplayId, lotId]);

  useEffect(() => {
    setDownloadFilename(getDefaultFilename());
  }, [getDefaultFilename]);

  const fetchSessionsPage = useCallback(
    async (params: PaginationParams): Promise<PageResponse<ParkingSession>> => {
      if (!lotId) {
        return {
          content: [],
          page: 0,
          size: params.size,
          totalElements: 0,
          totalPages: 0,
          first: true,
          last: true,
          sortBy: params.sortBy,
          sortDirection: params.sortDirection,
        };
      }

      return sessionService.getSessionsByLotOffsetPaginated(lotId, params);
    },
    [lotId]
  );

  const fetchLotDisplayId = useCallback(async () => {
    if (!lotId) return;
    try {
      const response = await api.get(`/parkinglots/get-by-id/${lotId}`);
      const lotData = response.data;
      if (lotData?.data?.displayLotId) {
        setLotDisplayId(lotData.data.displayLotId);
      } else {
        setLotDisplayId(lotId);
      }
    } catch (error) {
      console.error("Error fetching lot display id:", error);
      setLotDisplayId(lotId || "");
    }
  }, [lotId]);

  const {
    items: sessions,
    totalElements,
    totalPages,
    page,
    loading: paginationLoading,
    sortBy,
    sortDirection,
    setSort,
    setSearch,
    goToPage,
    refresh,
  } = usePagination<ParkingSession>({
    fetchFn: fetchSessionsPage,
    initialPage: 0,
    initialSize: TABLE_PAGE_SIZE,
    initialSortBy: "entryTime",
    initialSortDirection: "desc",
    enabled: !!lotId,
  });

  useEffect(() => {
    if (!lotId) return;
    fetchLotDisplayId();
    refresh();
  }, [lotId, fetchLotDisplayId, refresh]);

  const isLoading = paginationLoading;

  useEffect(() => {
    if (!sortBy) return;
    setSortConfig({
      key: sortBy as SortKey,
      direction: sortDirection === "asc" ? "ascending" : "descending",
    });
  }, [sortBy, sortDirection]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    setSearch(query);
    goToPage(0);
  };

  const handleSort = (key: SortKey) => {
    const targetDirection =
      sortConfig.key === key && sortConfig.direction === "descending"
        ? "ascending"
        : "descending";
    setSortConfig({ key, direction: targetDirection });
    setSort(key, targetDirection === "descending" ? "desc" : "asc");
    goToPage(0);
  };

  const getDurationMs = (session: ParkingSession) => {
    const start = new Date(session.entryTime).getTime();
    const end = session.exitTime ? new Date(session.exitTime).getTime() : Date.now();
    return Math.max(end - start, 0);
  };

  const formatDurationLabel = (session: ParkingSession) => {
    const durationMs = getDurationMs(session);
    const minutes = Math.floor(durationMs / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  const getSessionStatus = (session: ParkingSession): string => {
    if (session.forceEnd) return "Force End";
    return session.departed ? "Departed" : "Active";
  };

  const getDisplayStatus = (session: ParkingSession): string => {
    const status = getSessionStatus(session);
    if (!isMobile) return status;
    if (status === "Force End") return "Ended";
    if (status === "Departed") return "Left";
    return status;
  };

  const filteredResults = sessions;
  const paginatedEntries = sessions;

  const getUniqueKey = (session: ParkingSession) => session.sessionId;

  const selectedSessionStatus = selectedSession ? getSessionStatus(selectedSession) : null;
  const selectedSessionDisplayStatus = selectedSession ? getDisplayStatus(selectedSession) : null;

  const handleCheckboxChange = (session: ParkingSession) => {
    const key = getUniqueKey(session);
    setSelectedEntries((prev) => {
      const updated = new Set(prev);
      if (updated.has(key)) {
        updated.delete(key);
      } else {
        updated.add(key);
      }
      return updated;
    });
  };

  const handleSelectAll = () => {
    if (!filteredResults.length) return;
    const allSelected = selectedEntries.size === filteredResults.length;
    if (allSelected) {
      setSelectedEntries(new Set());
    } else {
      const keys = filteredResults.map((session) => getUniqueKey(session));
      setSelectedEntries(new Set(keys));
    }
  };

  const getSelectedEntriesForDownload = () => {
    if (selectedEntries.size === 0) {
      return filteredResults;
    }
    return filteredResults.filter((session) => selectedEntries.has(getUniqueKey(session)));
  };

  const handleDownload = (filename: string) => {
    const safeFilename = filename.trim() || getDefaultFilename();
    const header =
      "SessionID,PlateNumber,LotID,EntryTime,ExitTime,Departed,ForceEnd,TotalAmount,Currency,OwnerName,EntryAlprID,ExitAlprID,AdvancedPricingEnabled,PricingID,VehicleRegistered,SessionType,UserType,VehicleType,DwellTime,DwellPeriodTimeScale,Duration,Status,PaymentStatus\n";
    const rows = getSelectedEntriesForDownload().map((session) => {
      const duration = formatDurationLabel(session);
      const status = getSessionStatus(session);
      const paymentStatus = getPaymentStatusDisplayText(session.paymentStatus);
      return `${session.sessionId},${session.plateNumber},${session.lotId},${session.entryTime},${
        session.exitTime || ""
      },${session.departed},${session.forceEnd},${session.totalAmount ?? ""},${session.currency || ""},${session.ownerName || ""},${session.entryAlprId || ""},${session.exitAlprId || ""},${
        session.advancedPricingEnabled ?? ""
      },${session.pricingId || ""},${session.vehicleRegistered ?? ""},${session.sessionType || ""},${
        session.userType || ""
      },${session.vehicleType || ""},${session.dwellTime || ""},${session.dwellPeriodTimeScale || ""},${duration},${status},${paymentStatus}`;
    });
    const blob = new Blob([header + rows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = safeFilename.endsWith(".csv") ? safeFilename : `${safeFilename}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    setShowDownloadModal(false);
  };

  const handleOpenDownloadModal = () => {
    setDownloadFilename(getDefaultFilename());
    setShowDownloadModal(true);
  };

  const handleRowClick = (session: ParkingSession) => {
    setSelectedSession(session);
    setShowDetailModal(true);
  };

  const handleDownloadSessionData = (session: ParkingSession) => {
    const headers = Object.keys(session);
    const values = Object.values(session).map((value, index) => {
      const header = headers[index];
      if (header.toLowerCase().includes("time") && typeof value === "string") {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed.toLocaleString();
        }
      }
      if (typeof value === "boolean") {
        return value ? "Yes" : "No";
      }
      if (value === null || value === undefined) {
        return "";
      }
      let stringValue = String(value);
      if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
        stringValue = `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    });
    const csvContent = `${headers.join(",")}\n${values.join(",")}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${session.plateNumber || "Session"}_${session.sessionId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleForceEndSession = async () => {
    if (!selectedSession || !lotId) return;
    try {
      setIsForceEnding(true);
      const currentTime = new Date().toISOString();
      await api.put("/parkingsessions/force-end-session", null, {
        params: {
          lotId,
          sessionId: selectedSession.sessionId,
          forceEndedBy: "superadmin",
          exitTime: currentTime,
        },
      });
      setShowDetailModal(false);
      setSelectedSession(null);
      refresh();
    } catch (error) {
      console.error("Error force ending session:", error);
    } finally {
      setIsForceEnding(false);
    }
  };

  const handlePaymentStatusUpdate = async (newStatus: string) => {
    if (!selectedSession) return;
    try {
      setIsUpdatingPaymentStatus(true);
      const response = await api.put("/parkingsessions/override-payment-status", {
        sessionId: selectedSession.sessionId,
        paymentStatus: newStatus,
        modifiedBy: "superadmin",
      });
      const updated = response?.data?.data as ParkingSession | undefined;
      if (updated) {
        setSelectedSession(updated);
        refresh();
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
    } finally {
      setIsUpdatingPaymentStatus(false);
    }
  };

  const paymentStatusMenuItems: MenuItem[] = useMemo(() => {
    if (!selectedSession) return [];
    return PAYMENT_STATUS_OPTIONS.map((option) => ({
      key: option.value,
      label: option.label,
      selected: selectedSession.paymentStatus === option.value,
      onSelect: () => handlePaymentStatusUpdate(option.value),
    }));
  }, [selectedSession]);

  const downloadDescription =
    selectedEntries.size > 0 && selectedEntries.size < filteredResults.length
      ? `Download (${selectedEntries.size})`
      : "Download All";

  return (
    <div className="sessions-page">
      <div className="sessions-header">
        <div>
          <h1>Session Log</h1>
        </div>
        <button
          className="info-button"
          type="button"
          onClick={() => setShowInfoModal(true)}
          title="Learn about statuses"
        >
          <img src="/assets/info.svg" alt="Info" />
        </button>
      </div>

      <div className="sessions-search-actions">
        <div className="search-bar">
          <img src="/assets/SearchBarIcon.svg" alt="Search" />
          <input
            type="text"
            placeholder="Search plate, session id, payment status..."
            value={searchQuery}
            onChange={handleSearch}
          />
        </div>
        <div className="sessions-actions">
          <button
            type="button"
            className="button-secondary"
            onClick={handleOpenDownloadModal}
            disabled={!filteredResults.length}
          >
            <img src="/assets/Download.svg" alt="Download" />
            <span>{downloadDescription}</span>
          </button>
        </div>
      </div>

      <div className="table-container app-table-secondary-hover sessions-table-container">
        <table className="app-table sessions-table">
          <thead>
            <tr>
              <th className="sessions-checkbox-column">
                <label className="custom-checkbox" aria-label="Select all sessions">
                  <input
                    type="checkbox"
                    checked={selectedEntries.size === filteredResults.length && filteredResults.length > 0}
                    onChange={handleSelectAll}
                    className="hidden-checkbox"
                  />
                  <span className="checkbox-styler" />
                </label>
              </th>
              <th onClick={() => handleSort("plateNumber")}>
                Plate
                <img
                  src={
                    sortConfig.key === "plateNumber"
                      ? "/assets/tables/SortArrowSelected.svg"
                      : "/assets/tables/SortArrow.svg"
                  }
                  alt=""
                  className={`sort-arrow ${
                    sortConfig.key === "plateNumber" && sortConfig.direction === "descending" ? "desc" : ""
                  }`}
                />
              </th>
              <th onClick={() => handleSort("duration")}>
                Duration
                <img
                  src={
                    sortConfig.key === "duration"
                      ? "/assets/tables/SortArrowSelected.svg"
                      : "/assets/tables/SortArrow.svg"
                  }
                  alt=""
                  className={`sort-arrow ${
                    sortConfig.key === "duration" && sortConfig.direction === "descending" ? "desc" : ""
                  }`}
                />
              </th>
              <th onClick={() => handleSort("status")}>
                Status
                <img
                  src={
                    sortConfig.key === "status"
                      ? "/assets/tables/SortArrowSelected.svg"
                      : "/assets/tables/SortArrow.svg"
                  }
                  alt=""
                  className={`sort-arrow ${
                    sortConfig.key === "status" && sortConfig.direction === "descending" ? "desc" : ""
                  }`}
                />
              </th>
              <th onClick={() => handleSort("entryTime")}>
                Entry Date
                <img
                  src={
                    sortConfig.key === "entryTime"
                      ? "/assets/tables/SortArrowSelected.svg"
                      : "/assets/tables/SortArrow.svg"
                  }
                  alt=""
                  className={`sort-arrow ${
                    sortConfig.key === "entryTime" && sortConfig.direction === "descending" ? "desc" : ""
                  }`}
                />
              </th>
              <th onClick={() => handleSort("totalAmount")}>
                Total
                <img
                  src={
                    sortConfig.key === "totalAmount"
                      ? "/assets/tables/SortArrowSelected.svg"
                      : "/assets/tables/SortArrow.svg"
                  }
                  alt=""
                  className={`sort-arrow ${
                    sortConfig.key === "totalAmount" && sortConfig.direction === "descending" ? "desc" : ""
                  }`}
                />
              </th>
              {!isMobile && (
                <th onClick={() => handleSort("paymentStatus")}>
                  Payment Status
                  <img
                    src={
                      sortConfig.key === "paymentStatus"
                        ? "/assets/tables/SortArrowSelected.svg"
                        : "/assets/tables/SortArrow.svg"
                    }
                    alt=""
                    className={`sort-arrow ${
                      sortConfig.key === "paymentStatus" && sortConfig.direction === "descending"
                        ? "desc"
                        : ""
                    }`}
                  />
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedEntries.map((session) => {
              const checkboxKey = getUniqueKey(session);
              const isChecked = selectedEntries.has(checkboxKey);
              const entryDate = new Date(session.entryTime);
              const sessionStatus = getSessionStatus(session);
              const displayStatus = getDisplayStatus(session);
              return (
                <tr key={session.sessionId} className="sessions-row" onClick={() => handleRowClick(session)}>
                  <td className="sessions-checkbox-column" onClick={(e) => e.stopPropagation()}>
                    <label className="custom-checkbox" aria-label={`Select ${session.plateNumber}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleCheckboxChange(session)}
                        className="hidden-checkbox"
                      />
                      <span className="checkbox-styler" />
                    </label>
                  </td>
                  <td>
                    <div className="sessions-plate">
                      <span className="plate-badge">{session.plateNumber || "N/A"}</span>
                      {session.userType === "Violator" && (
                        <img
                          src="/assets/superadmin/sessions/violator.svg"
                          alt="Violator"
                          className="sessions-violator-icon"
                        />
                      )}
                    </div>
                  </td>
                  <td>{formatDurationLabel(session)}</td>
                  <td>
                    <span className={`sessions-status-chip ${sessionStatus.toLowerCase().replace(" ", "-")}`}>
                      {displayStatus}
                    </span>
                  </td>
                  <td>
                    <div>{entryDate.toLocaleDateString()}</div>
                    <div className="sessions-subtext">{entryDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  </td>
                  <td>{formatSessionAmount(session.totalAmount, session.currency)}</td>
                  {!isMobile && (
                    <td>
                      <span className={`sessions-payment-chip payment-${getPaymentStatusColor(session.paymentStatus)}`}>
                        {getPaymentStatusDisplayText(session.paymentStatus)}
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!filteredResults.length && !isLoading && (
        <div className="sessions-empty-state">
          <p>No sessions found</p>
        </div>
      )}

      {isLoading && <div className="sessions-loading">Loading sessions...</div>}

      {filteredResults.length > 0 && (
        <div className="app-table-footer sessions-footer">
          <div className="sessions-footer-left">
            <span>
              Showing {paginatedEntries.length} of {totalElements} entries
            </span>
          </div>
          <Pagination
            currentPage={page}
            totalPages={Math.max(totalPages, 1)}
            totalItems={totalElements}
            itemsOnPage={paginatedEntries.length}
            onPageChange={goToPage}
          />
        </div>
      )}

      <Modal
        isOpen={showDownloadModal}
        title="Download Session CSV"
        confirmText="Download CSV"
        onConfirm={() => handleDownload(downloadFilename)}
        onCancel={() => setShowDownloadModal(false)}
        disableConfirm={!filteredResults.length}
        customContent={
          <div className="modal-form">
            <label>
              Filename
              <input
                value={downloadFilename}
                onChange={(e) => setDownloadFilename(e.target.value)}
                placeholder="session-log.csv"
              />
            </label>
            <p>{downloadDescription}</p>
          </div>
        }
      />

      <Modal
        isOpen={showDetailModal && Boolean(selectedSession)}
        title="Session Details"
        onCancel={() => setShowDetailModal(false)}
        hideButtons
        customContent={
          selectedSession && (
            <div className="session-detail">
              <div className="session-detail-header">
                <div className="session-detail-header-content">
                  <div className="session-detail-plate">
                    <span className="plate-badge">{selectedSession.plateNumber}</span>
                  </div>
                  <div className="session-detail-status-row">
                    {selectedSessionStatus && selectedSessionDisplayStatus && (
                      <span
                        className={`sessions-status-chip ${selectedSessionStatus
                          .toLowerCase()
                          .replace(" ", "-")}`}
                      >
                        {selectedSessionDisplayStatus}
                      </span>
                    )}
                    <div className="session-detail-payment-group">
                      <span
                        className={`sessions-payment-chip payment-${getPaymentStatusColor(
                          selectedSession.paymentStatus
                        )}`}
                      >
                        {getPaymentStatusDisplayText(selectedSession.paymentStatus)}
                      </span>
                      <Menu
                        trigger={
                          <button
                            type="button"
                            className="session-payment-edit inline-edit"
                            disabled={isUpdatingPaymentStatus}
                          >
                            Edit
                          </button>
                        }
                        items={paymentStatusMenuItems}
                        placement="bottom-start"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="session-detail-grid">
                <div className="session-detail-column">
                  <div>
                    <span className="session-detail-label">Date & Time:</span>
                    <span>{new Date(selectedSession.entryTime).toLocaleString()}</span>
                  </div>
                  {selectedSession.exitTime && (
                    <div>
                      <span className="session-detail-label">Exit Time:</span>
                      <span>{new Date(selectedSession.exitTime).toLocaleString()}</span>
                    </div>
                  )}
                  <div>
                    <span className="session-detail-label">Duration:</span>
                    <span>{formatDurationLabel(selectedSession)}</span>
                  </div>
                  <div>
                    <span className="session-detail-label">Total Amount:</span>
                    <span>
                      {formatSessionAmount(selectedSession.totalAmount, selectedSession.currency)}
                    </span>
                  </div>
                  <div>
                    <span className="session-detail-label">Session Type:</span>
                    <span>{selectedSession.sessionType || "N/A"}</span>
                  </div>
                  <div>
                    <span className="session-detail-label">User Type:</span>
                    <span>{selectedSession.userType || "N/A"}</span>
                  </div>
                </div>
                <div className="session-detail-column">
                  <div>
                    <span className="session-detail-label">Advanced Pricing:</span>
                    <span className="session-detail-value">
                      {selectedSession.advancedPricingEnabled ? "Yes" : "No"}
                      <img
                        src={
                          selectedSession.advancedPricingEnabled
                            ? "/assets/superadmin/sessions/check.svg"
                            : "/assets/superadmin/sessions/x.svg"
                        }
                        alt={selectedSession.advancedPricingEnabled ? "Yes" : "No"}
                      />
                    </span>
                  </div>
                  <div>
                    <span className="session-detail-label">Vehicle Registered:</span>
                    <span className="session-detail-value">
                      {selectedSession.vehicleRegistered ? "Yes" : "No"}
                      <img
                        src={
                          selectedSession.vehicleRegistered
                            ? "/assets/superadmin/sessions/check.svg"
                            : "/assets/superadmin/sessions/x.svg"
                        }
                        alt={selectedSession.vehicleRegistered ? "Yes" : "No"}
                      />
                    </span>
                  </div>
                  <div>
                    <span className="session-detail-label">Vehicle Type:</span>
                    <span>{selectedSession.vehicleType || "N/A"}</span>
                  </div>
                </div>
                <div className="session-detail-column session-detail-column-muted">
                  <div>
                    <span className="session-detail-label">Lot ID:</span>
                    <span className="session-detail-value">
                      {selectedSession.lotId || "N/A"}
                      {selectedSession.lotId && (
                        <button
                          type="button"
                          className="copy-button"
                          onClick={() => handleCopy(selectedSession.lotId, "session-lot-id")}
                          aria-label={
                            copiedKey === "session-lot-id" ? "Lot ID copied" : "Copy Lot ID"
                          }
                          title={copiedKey === "session-lot-id" ? "Copied!" : "Copy Lot ID"}
                        >
                          <img
                            src={
                              copiedKey === "session-lot-id"
                                ? "/assets/copy-check.svg"
                                : "/assets/copy.svg"
                            }
                            alt={copiedKey === "session-lot-id" ? "Copied" : "Copy"}
                          />
                        </button>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="session-detail-label">Session ID:</span>
                    <span className="session-detail-value">
                      {selectedSession.sessionId || "N/A"}
                      {selectedSession.sessionId && (
                        <button
                          type="button"
                          className="copy-button"
                          onClick={() => handleCopy(selectedSession.sessionId, "session-id")}
                          aria-label={
                            copiedKey === "session-id" ? "Session ID copied" : "Copy Session ID"
                          }
                          title={copiedKey === "session-id" ? "Copied!" : "Copy Session ID"}
                        >
                          <img
                            src={
                              copiedKey === "session-id"
                                ? "/assets/copy-check.svg"
                                : "/assets/copy.svg"
                            }
                            alt={copiedKey === "session-id" ? "Copied" : "Copy"}
                          />
                        </button>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="session-detail-label">Entry ALPR ID:</span>
                    <span className="session-detail-value">
                      {selectedSession.entryAlprId || "N/A"}
                      {selectedSession.entryAlprId && (
                        <button
                          type="button"
                          className="copy-button"
                          onClick={() =>
                            handleCopy(selectedSession.entryAlprId, "session-entry-alpr-id")
                          }
                          aria-label={
                            copiedKey === "session-entry-alpr-id"
                              ? "Entry ALPR ID copied"
                              : "Copy Entry ALPR ID"
                          }
                          title={
                            copiedKey === "session-entry-alpr-id"
                              ? "Copied!"
                              : "Copy Entry ALPR ID"
                          }
                        >
                          <img
                            src={
                              copiedKey === "session-entry-alpr-id"
                                ? "/assets/copy-check.svg"
                                : "/assets/copy.svg"
                            }
                            alt={copiedKey === "session-entry-alpr-id" ? "Copied" : "Copy"}
                          />
                        </button>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="session-detail-label">Exit ALPR ID:</span>
                    <span className="session-detail-value">
                      {selectedSession.exitAlprId || "N/A"}
                      {selectedSession.exitAlprId && (
                        <button
                          type="button"
                          className="copy-button"
                          onClick={() =>
                            handleCopy(selectedSession.exitAlprId, "session-exit-alpr-id")
                          }
                          aria-label={
                            copiedKey === "session-exit-alpr-id"
                              ? "Exit ALPR ID copied"
                              : "Copy Exit ALPR ID"
                          }
                          title={
                            copiedKey === "session-exit-alpr-id" ? "Copied!" : "Copy Exit ALPR ID"
                          }
                        >
                          <img
                            src={
                              copiedKey === "session-exit-alpr-id"
                                ? "/assets/copy-check.svg"
                                : "/assets/copy.svg"
                            }
                            alt={copiedKey === "session-exit-alpr-id" ? "Copied" : "Copy"}
                          />
                        </button>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="session-detail-label">Pricing ID:</span>
                    <span className="session-detail-value">
                      {selectedSession.pricingId || "N/A"}
                      {selectedSession.pricingId && (
                        <button
                          type="button"
                          className="copy-button"
                          onClick={() => handleCopy(selectedSession.pricingId, "session-pricing-id")}
                          aria-label={
                            copiedKey === "session-pricing-id"
                              ? "Pricing ID copied"
                              : "Copy Pricing ID"
                          }
                          title={
                            copiedKey === "session-pricing-id" ? "Copied!" : "Copy Pricing ID"
                          }
                        >
                          <img
                            src={
                              copiedKey === "session-pricing-id"
                                ? "/assets/copy-check.svg"
                                : "/assets/copy.svg"
                            }
                            alt={copiedKey === "session-pricing-id" ? "Copied" : "Copy"}
                          />
                        </button>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="session-detail-label">Owner ID:</span>
                    <span className="session-detail-value">
                      {selectedSession.ownerName || "N/A"}
                      {selectedSession.ownerName && (
                        <button
                          type="button"
                          className="copy-button"
                          onClick={() => handleCopy(selectedSession.ownerName, "session-owner-id")}
                          aria-label={
                            copiedKey === "session-owner-id" ? "Owner ID copied" : "Copy Owner ID"
                          }
                          title={copiedKey === "session-owner-id" ? "Copied!" : "Copy Owner ID"}
                        >
                          <img
                            src={
                              copiedKey === "session-owner-id"
                                ? "/assets/copy-check.svg"
                                : "/assets/copy.svg"
                            }
                            alt={copiedKey === "session-owner-id" ? "Copied" : "Copy"}
                          />
                        </button>
                      )}
                    </span>
                  </div>
                </div>
              </div>
              <div className="session-detail-actions">
                <button
                  type="button"
                  className="button-secondary"
                  onClick={() => handleDownloadSessionData(selectedSession)}
                >
                  {isMobile ? "Download" : "Download record"}
                </button>
                {selectedSessionStatus === "Active" && (
                  <button
                    type="button"
                    className="button-danger"
                    onClick={handleForceEndSession}
                    disabled={isForceEnding}
                  >
                    {isForceEnding ? "Force Ending..." : isMobile ? "Force End" : "Force End Session"}
                  </button>
                )}
              </div>
            </div>
          )
        }
      />

      <Modal
        isOpen={showInfoModal}
        title=""
        onCancel={() => setShowInfoModal(false)}
        hideButtons
        customContent={
          <div className="session-info-modal">
            <div className="session-modal-columns">
              <div>
                <h2>Session Statuses</h2>
                <p className="session-modal-row">
                  <span className="sessions-status-chip active">Active</span>
                  <span>Session in progress</span>
                </p>
                <p className="session-modal-row">
                  <span className="sessions-status-chip departed">{isMobile ? "Left" : "Departed"}</span>
                  <span>Session completed normally</span>
                </p>
                <p className="session-modal-row">
                  <span className="sessions-status-chip force-end">{isMobile ? "Ended" : "Force End"}</span>
                  <span>Manually terminated</span>
                </p>
              </div>
              <div>
                <h2>Payment Statuses</h2>
                <p className="session-modal-row">
                  <span className="sessions-payment-chip payment-green">No Charge / Paid</span>
                  <span>Payment completed successfully</span>
                </p>
                <p className="session-modal-row">
                  <span className="sessions-payment-chip payment-yellow">Pending</span>
                  <span>Awaiting payment</span>
                </p>
                <p className="session-modal-row">
                  <span className="sessions-payment-chip payment-yellow">Processing</span>
                  <span>Processing or active session</span>
                </p>
                <p className="session-modal-row">
                  <span className="sessions-payment-chip payment-red">Unpaid / Failed</span>
                  <span>Needs attention from your team</span>
                </p>
                <p className="session-modal-row">
                  <span className="sessions-payment-chip payment-red">Disputed / Refunded</span>
                  <span>Refunds, disputes, and chargebacks</span>
                </p>
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default Sessions;
